import { Typography, Box, Chip } from "@mui/material"
import { Conditional, Stack } from "src/shared/components"
import SearchIcon from "@mui/icons-material/Search"
import { getLocationListTableColumns } from "./getLocationListColumns"
import { DataGridMui } from "src/shared/container/data-grid-mui"
import {
  FilterCondition,
  FilterOperator,
  LogicalOperator,
  SortDirection,
  useGetLocationsListQuery
} from "src/graphql/quark/quark.type"
import { useMemo, useState } from "react"
import DebouncedInput from "src/shared/components/debounced-Input"
import EditUpdateLocationFormModal from "../../components/EditUpdateLocationFormModal"
import ControlPointIcon from "@mui/icons-material/ControlPoint"
import { useSearchParams } from "react-router-dom"
import { usePermission } from "src/shared/hooks/usePermission"
import { useTranslation } from "react-i18next"

const LocationList = () => {
  const { t } = useTranslation("system")
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 20 })
  const [filters, setFilters] = useState<{ id: string; value: string }[]>([])
  const [sorting, setSorting] = useState<{ id?: null | string; desc?: null | boolean }>({
    id: "secondaryId",
    desc: true
  })
  const { hasPermission } = usePermission()
  const [searchParams, setSearchParams] = useSearchParams()

  const filterConditions: FilterCondition[] = useMemo(() => {
    if (!filters?.length) return []
    return filters.map(
      (val) =>
        ({
          operator: val.id === "secondaryId" ? FilterOperator.Equal : FilterOperator.Like,
          field: val.id,
          value: val.value
        }) as FilterCondition
    )
  }, [filters])

  const { data, loading, refetch } = useGetLocationsListQuery({
    fetchPolicy: "network-only",
    variables: {
      limit: pagination.pageSize,
      page: pagination.pageIndex + 1,
      filter: {
        conditions: filterConditions,
        logicalOperator: LogicalOperator.And
      },
      sort: sorting?.id
        ? [
            {
              direction: sorting?.desc ? SortDirection.Desc : SortDirection.Asc,
              field: sorting.id as string
            }
          ]
        : []
    }
  })

  const handleCreateLocation = () => {
    searchParams.set("mode", "create")
    setSearchParams(searchParams)
  }

  const handleEditLocation = (location: any) => {
    searchParams.set("mode", "edit")
    searchParams.set("id", location.id)
    setSearchParams(searchParams)
  }

  const columns = getLocationListTableColumns({ onEdit: handleEditLocation, hasPermission })

  return (
    <Stack height={"100%"} p={2}>
      <Stack mb={3}>
        {/* <Box display="flex" justifyContent="space-between" alignItems="center"> */}
        <Box>
          <Typography variant="h3">{t("locations.title")}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t("locations.subtitle")}
          </Typography>
          {/* </Box> */}
        </Box>
      </Stack>

      <DataGridMui
        id="settings-locations-list-table"
        columns={columns}
        data={data?.getLocations?.data ?? []}
        enableColumnFilters={false}
        enableSorting={false}
        skeletonCount={20}
        initialPageSize={20}
        rowsPerPageOptions={[20, 30, 50, 100]}
        isLoading={loading}
        onRefresh={refetch}
        paginationModel={pagination}
        paginationMode="server"
        sortingMode="server"
        filterMode="server"
        sortingModel={sorting as any}
        onSortModelChange={setSorting}
        onFilterChange={setFilters}
        onPaginationChange={setPagination}
        leftComponent={
          <DebouncedInput
            variant="rounded"
            value={(filters?.find((val) => val.id === "name")?.value ?? "") as string}
            onChange={(val) => {
              if (val === undefined) return

              setFilters((prev) => {
                const data = [...prev]
                const nameIndex = data?.findIndex((prev) => prev.id === "name")
                if (nameIndex === -1) {
                  data.push({ id: "name", value: val as string })
                  return data
                } else {
                  if (!val) return data.filter((prev) => prev.id !== "name")

                  data[nameIndex].value = val as string
                  return data
                }
              })
            }}
            startAdornment={<SearchIcon />}
            placeholder={t("locations.searchPlaceholder")}
            size="small"
          />
        }
        rightComponent={
          <Stack my={2} spacing={2} direction={"row"} alignItems={"center"}>
            <Conditional condition={hasPermission("system:location:create")}>
              <Chip
                size="small"
                onClick={handleCreateLocation}
                color="primary"
                clickable
                variant="outlined"
                icon={<ControlPointIcon fontSize="small" />}
                label={t("locations.createButton")}
              />
            </Conditional>
          </Stack>
        }
      />
      <Conditional condition={hasPermission("system:location:create") || hasPermission("system:location:update")}>
        <EditUpdateLocationFormModal />
      </Conditional>
    </Stack>
  )
}

//

export default LocationList
